import { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  CheckCircleIcon,
  Chip,
  Field,
  Input,
  Spinner,
  Stack,
  Text,
  Textarea,
} from '@billda/design-system';
import { FormProvider } from 'react-hook-form';
import { useSitePanelState } from '../../../hooks/useSitePanelState';
import { usePlotDetails } from '../../../hooks/usePlotDetails';
import { useAddLabourItemForm } from '../../../hooks/useAddLabourItemForm';
import { LabourItemAutocomplete } from '../../organisms/LabourItemAutocomplete';
import { PriceInputField } from '../../molecules/PriceInputField';
import { ConfirmAddLabourItemDialog } from '../../molecules/ConfirmAddLabourItemDialog';

interface AddLabourItemPanelProps {
  plotId: number;
}

interface RecentlyAddedItem {
  name: string;
  price: number;
}

/**
 * Panel for adding labour items to a plot
 * Follows panel pattern like LabourItemDetailsPanel
 */
export function AddLabourItemPanel({ plotId }: AddLabourItemPanelProps) {
  const { siteId, closePanel } = useSitePanelState();
  const siteIdNum = siteId ? parseInt(siteId, 10) : 0;
  const { data: plot } = usePlotDetails(siteIdNum || null, plotId);
  const [recentlyAdded, setRecentlyAdded] = useState<RecentlyAddedItem[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);

  const {
    form,
    handleSubmit: handleFormSubmit,
    isSubmitting,
    successMessage,
    errorMessage,
  } = useAddLabourItemForm(siteIdNum, plotId, {
    onSuccess: () => {
      // Close panel after successful submission
      closePanel();
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
  } = form;

  const selectedDefinition = watch('selectedDefinition');

  // Clear state when panel mounts (fresh open)
  useEffect(() => {
    setRecentlyAdded([]);
    setIsCreatingNew(false);
  }, []);

  // Handle form submission - show confirmation dialog
  const onSubmit = async (data: any) => {
    setPendingData(data);
    setShowConfirm(true);
  };

  // Handle confirmation - actually submit the form
  const handleConfirm = async () => {
    setShowConfirm(false);
    if (pendingData) {
      await handleFormSubmit(pendingData);

      // Add to recently added list if successful
      if (successMessage) {
        const newItem = {
          name: isCreatingNew
            ? pendingData.name
            : pendingData.selectedDefinition?.name,
          price: pendingData.price,
        };
        setRecentlyAdded(prev => [newItem, ...prev].slice(0, 3));
      }
    }
  };

  // Handle cancel confirmation
  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setPendingData(null);
  };

  // No plot data (initial loading or truly no data)
  if (!plot) {
    return (
      <div className="p-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-6">
      {/* Success/Error messages */}
      {successMessage && (
        <Alert severity="success" className="mb-4">
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" className="mb-4">
          {errorMessage}
        </Alert>
      )}

      {/* Info banner */}
      {!isCreatingNew && (
        <Alert severity="info" className="mb-6">
          Select an existing labour item type to add to this plot
        </Alert>
      )}

      {/* Form */}
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-1 flex-col"
        >
          <Stack spacing={3} className="flex-1">
            {/* Mode: Select existing */}
            {!isCreatingNew && (
              <>
                <div>
                  <LabourItemAutocomplete
                    value={selectedDefinition ?? null}
                    onChange={value => {
                      form.setValue('selectedDefinition', value as any);
                    }}
                    error={errors.selectedDefinition?.message?.toString()}
                  />
                </div>
                <div>
                  <button
                    type="button"
                    className="cursor-pointer type-body-md text-content-primary no-underline hover:text-content-accent"
                    onClick={() => {
                      setIsCreatingNew(true);
                      form.setValue('selectedDefinition', null);
                    }}
                  >
                    Or create a new labour item type
                  </button>
                </div>
              </>
            )}

            {/* Mode: Create new */}
            {isCreatingNew && (
              <>
                <Alert severity="info">
                  You are creating a new labour item type. This will be
                  available for future use across all plots.
                </Alert>
                <Field
                  label="Labour Item Name *"
                  error={errors.name?.message?.toString()}
                >
                  <Input invalid={!!errors.name} {...register('name')} />
                </Field>
                <div>
                  <button
                    type="button"
                    className="cursor-pointer type-body-md text-content-primary no-underline hover:text-content-accent"
                    onClick={() => {
                      setIsCreatingNew(false);
                      form.setValue('name', undefined);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {/* Price field */}
            <PriceInputField />

            {/* Optional description */}
            <Field
              label="Description (Optional)"
              error={errors.description?.message?.toString()}
            >
              <Textarea
                rows={3}
                invalid={!!errors.description}
                {...register('description')}
              />
            </Field>
          </Stack>

          {/* Recently added items */}
          {recentlyAdded.length > 0 && (
            <div className="mt-6">
              <Text variant="caption" color="secondary" className="mb-1 block">
                Recently Added
              </Text>
              <Stack spacing={1}>
                {recentlyAdded.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 rounded-lg bg-surface-success-subtle p-2"
                  >
                    <CheckCircleIcon
                      className="size-4 text-content-success"
                      aria-hidden="true"
                    />
                    <Text variant="body2" className="flex-1">
                      {item.name}
                    </Text>
                    <Chip label={`£${item.price.toFixed(2)}`} size="sm" />
                  </div>
                ))}
              </Stack>
            </div>
          )}

          {/* Plot Information */}
          <div className="mt-4 rounded-lg bg-surface-raised p-4">
            <Text variant="body2" color="secondary">
              Adding to:{' '}
              <strong>
                {plot.name} ({plot.plotNumber})
              </strong>
            </Text>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-4">
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
              fullWidth
            >
              Add Labour Item
            </Button>
            <Button
              variant="outline"
              onClick={() => closePanel()}
              disabled={isSubmitting}
            >
              Close
            </Button>
          </div>
        </form>
      </FormProvider>

      {/* Confirmation Dialog */}
      <ConfirmAddLabourItemDialog
        open={showConfirm}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirm}
        labourItemName={
          isCreatingNew
            ? pendingData?.name
            : pendingData?.selectedDefinition?.name
        }
        plotName={plot?.name}
        plotNumber={plot?.plotNumber}
      />
    </div>
  );
}
